import {
  Component,
  OnInit,
  ViewChildren,
  ViewChild,
  AfterViewChecked,
  AfterViewInit,
  QueryList,
  ElementRef,
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";

import { Action } from "./shared/model/action";
import { Event } from "./shared/model/event";
import { Message } from "./shared/model/message";
import { User } from "./shared/model/user";
import { SocketService } from "./shared/services/socket.service";
import { StoreUserService } from "./shared/services/store-user.service";
import { randomColor } from "randomcolor";

@Component({
  selector: "app-chat",
  templateUrl: "./chat.component.html",
  styleUrls: ["./chat.component.scss"],
})
export class ChatComponent implements OnInit, AfterViewInit {
  action = Action;
  user: User;
  messages: Message[] = [];
  ioConnection: any;

  title: string = "WEBCHAT";

  // HACK: TODO, do this via CSS properly?
  isMobile: boolean = window.innerWidth <= 500;
  chatVisible: boolean = !this.isMobile;

  public setTitle(title): void {
    this.title = title;
  }

  // Ensure chat visibility (used by parent components)
  public setChatVisibility(visible: boolean): void {
    this.chatVisible = visible;
  }

  wasManuallyScrolled: boolean = false;

  msgSendFormControl = new FormControl("", [Validators.required]);
  usernameFormControl = new FormControl("", [Validators.required]);
  previousUsername: string;

  toggleShowChat() {
    this.chatVisible = !this.chatVisible;
  }

  private chatMsgInputForm: ElementRef;

  @ViewChild("chatMsgInputForm") set content(content: ElementRef) {
    if (content) {
      // initially setter gets called with undefined
      this.chatMsgInputForm = content;
    }
  }

  @ViewChild("chatMsgInputFormMobile") set contentB(contentB: ElementRef) {
    if (contentB) {
      // initially setter gets called with undefined
      this.chatMsgInputForm = contentB;
    }
  }

  @ViewChild("userNameInput") private userNameInputForm: ElementRef;

  @ViewChild("chatMsgList") private chatMsgList: ElementRef;

  @ViewChildren("chatMsgEntry")
  private chatMsgListEntries: QueryList<ElementRef>;

  constructor(
    private socketService: SocketService,
    private storedUser: StoreUserService,
  ) {}

  unsetName() {
    this.user.name = "";
    setTimeout(() => this.userNameInputForm.nativeElement.focus(), 0);
  }

  public sendMsg(): void {
    this.debugMsg("SEND MSG HAPPENED");

    let message = this.msgSendFormControl.value;
    this.sendMessage(message);
    this.msgSendFormControl.reset();
  }

  public saveUsername(): void {
    this.debugMsg("SAVE USERNAME HAPPENED");

    let newUserName = this.usernameFormControl.value;
    if (newUserName) {
      this.user.name = newUserName;
      this.storedUser.storeUser(newUserName);
      this.previousUsername = newUserName;
      setTimeout(() => this.chatMsgInputForm.nativeElement.focus(), 0);
    }
  }

  ngOnInit(): void {
    this.initModel();
  }

  // auto-scroll fix: inspired by this stack overflow post
  // https://stackoverflow.com/questions/35232731/angular2-scroll-to-bottom-chat-style
  private scrollToBottom(): void {
    try {
      if (!this.wasManuallyScrolled) {
        this.chatMsgList.nativeElement.scrollTop =
          this.chatMsgList.nativeElement.scrollHeight;
      }
    } catch (err) {}
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
    // subscribing to any changes in the list of items / messages
    this.chatMsgListEntries.changes.subscribe((elements) => {
      this.scrollToBottom();
    });
  }

  private initModel(): void {
    const randomId = this.getRandomId();
    this.user = {
      id: randomId,
      name: this.storedUser.getStoredUser(),
    };
    this.initIoConnection();
  }

  public computeColor(m: Message): string {
    let d = new Date();
    let currentYear = d.getUTCFullYear();
    let currentMonth = d.getUTCMonth();
    let currentDay = d.getUTCDate();
    let shiftDay = (Math.abs(m.from.hash) % 29) + 1;
    if (currentDay < shiftDay) {
      currentMonth = currentMonth - 1;
    }
    let color = randomColor({
      seed: Math.abs(m.from.hash) + currentYear + currentMonth,
      luminosity: "light",
    });
    return color;
  }

  private initIoConnection(): void {
    this.socketService.initSocket();

    this.ioConnection = this.socketService
      .onMessage()
      .subscribe((message: Message) => {
        this.messages.push(message);
      });

    this.socketService.onEvent(Event.CONNECT).subscribe(() => {
      console.log("connected");
      this.messages = []; // server will re-push messages on reconnect
    });

    this.socketService.onEvent(Event.DISCONNECT).subscribe(() => {
      console.log("disconnected");
    });
  }

  private getRandomId(): number {
    return Math.floor(Math.random() * 1000000) + 1;
  }

  debugMsg(text: string) {
    let usr: User = {
      name: "DEBUG",
      hash: 10,
    };
    let msg: Message = {
      date: new Date(),
      from: usr,
      content: text,
    };

    //this.messages.push(msg);
    return msg;
  }

  public chatScroll(event): void {
    this.wasManuallyScrolled = true;
    let obj = event.target;

    let distFromBottom = Math.abs(
      obj.scrollTop - (obj.scrollHeight - obj.offsetHeight),
    );

    if (distFromBottom < 20) {
      this.wasManuallyScrolled = false;
    }
  }

  public sendMessage(message: string): void {
    this.wasManuallyScrolled = false;
    if (!message) {
      return;
    }

    let msg: Message = {
      date: new Date(),
      from: this.user,
      content: message,
    };

    let ME_PREFIX: string = "/me ";

    if (message.startsWith(ME_PREFIX)) {
      msg.action = Action.ME_MSG;
      msg.content = message.slice(ME_PREFIX.length);
    }

    this.socketService.send(msg);
  }

  public handleKeyUp($event): void {
    if ($event.keyCode != 13) {
      this.debugMsg("TEXT ENTERED");

      $event.stopPropagation();
    } else {
      this.debugMsg("ENTER KEY PRESSED");
    }
  }

  public onSubmit(): void {
    this.debugMsg("SUBMIT HAPPENED");

    this.saveUsername();
  }
}
